import React, { useCallback, useEffect, useRef, useState } from "react";
import { useZOrder } from "../../StateManagement";
import { Icon } from "semantic-ui-react";

type Props = {
	zId: string;
	title: string;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	rootStyle?: React.CSSProperties;
	contentClassName?: string;
	contentStyle?: React.CSSProperties;
};

export const DraggableWindow = ({
	zId,
	title,
	open,
	setOpen,
	rootStyle = {},
	contentClassName,
	contentStyle = {},
	children,
}: React.PropsWithChildren<Props>) => {
	const [isDraggingWindow, setIsDraggingWindow] = useState(false);
	const [relativeCoords, setRelativeCoords] = useState<[number, number]>([0, 0]);
	const { setTopWindow, getWindowOrder } = useZOrder();
	const lastPosRef = useRef<[number, number] | null>(null);

	const handlePointerUp = useCallback((e: PointerEvent) => {
		setIsDraggingWindow(false);
		lastPosRef.current = null;
		e.stopPropagation();
		e.preventDefault();
	}, []);

	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			const clientDelta = [e.clientX - (lastPosRef.current?.[0] ?? 0), e.clientY - (lastPosRef.current?.[1] ?? 0)];
			if (isDraggingWindow) {
				setRelativeCoords((prev) => [prev[0] + clientDelta[0], prev[1] + clientDelta[1]]);
				lastPosRef.current = [e.clientX, e.clientY];
			}
			e.stopPropagation();
			e.preventDefault();
		},
		[isDraggingWindow],
	);

	useEffect(() => {
		if (open) {
			setTopWindow(zId);
		}
	}, [open, setTopWindow, zId]);

	useEffect(() => {
		window.addEventListener("pointerup", handlePointerUp);

		window.addEventListener("pointermove", handlePointerMove);

		return () => {
			window.removeEventListener("pointerup", handlePointerUp);
			window.removeEventListener("pointermove", handlePointerMove);
		};
	}, [handlePointerMove, handlePointerUp, isDraggingWindow]);

	return open ? (
		<div
			className={`draggableContainer${isDraggingWindow ? " dragging" : ""}`}
			onMouseDown={() => {
				setTopWindow(zId);
			}}
			style={{
				translate: `${relativeCoords[0]}px ${relativeCoords[1]}px`,
				left: "144px",
				bottom: "1em",
				top: "unset",
				zIndex: 1060 + getWindowOrder(zId),
				...rootStyle,
			}}
		>
			<div
				className="popupBar"
				onPointerDown={({ clientX, clientY }) => {
					setIsDraggingWindow(true);
					lastPosRef.current = [clientX, clientY];
				}}
			>
				<span style={{ fontSize: 14, fontWeight: 500, marginLeft: 7, pointerEvents: "none", lineHeight: "25px" }}>
					{title}
				</span>
				<div />
				<Icon
					name="close"
					onClick={() => {
						setOpen(false);
					}}
				/>
			</div>
			<div className={`draggableContent${contentClassName ? " " + contentClassName : ""}`} style={contentStyle}>
				{children}
			</div>
		</div>
	) : (
		<></>
	);
};
